import { useSearchParams } from 'react-router-dom-v5-compat';

import { OnSubmitArgs } from 'new-components/DLS/OpForm/OpForm';
import { useOpMutation } from 'utils/customHooks/useOpMutation';

/**
 * Hook for RoleForm mutations.
 * @param {number} orgId - ID of the org
 * @param {number} roleId - ID of the role if in edit mode
 * @param {Boolean} isEditable - is the role editable (not a default role) - affects which calls we can make here.
 * @returns onSubmit handler for RoleForm
 */
export const useRoleFormMutation = ({
  orgId,
  roleId,
  isEditable,
}: {
  orgId: number;
  roleId?: number;
  isEditable?: boolean;
}) => {
  const [_, setSearchParams] = useSearchParams();

  /* Mutations for create and update */

  const setRoleScopeIdsMutation = useOpMutation({
    apiEndpointName: 'setRoleScopeIds',
    queryKeysToInvalidate: [['listRoleScopeIds']],
    catchMutateAsyncError: false,
  });

  const setRoleUserIdsMutation = useOpMutation({
    apiEndpointName: 'setRoleUserIds',
    queryKeysToInvalidate: [['listRoleUsers'], ['listRoles'], ['describeRole']],
    catchMutateAsyncError: false,
  });

  const setRoleSiteIdsMutation = useOpMutation({
    apiEndpointName: 'setRoleSiteIds',
    queryKeysToInvalidate: [['listRoles'], ['describeRole']],
    catchMutateAsyncError: false,
  });

  // For editable roles we call this so we only get one success message. See comment above updateRoleUserIdsMutationWitSuccessMessage.
  const updateRoleUserIdsMutation = useOpMutation({
    apiEndpointName: 'updateRoleUserIds',
    queryKeysToInvalidate: [['listRoleUsers'], ['listRoles'], ['describeRole']],
    catchMutateAsyncError: false,
  });

  // For non-editable roles (Default roles) we don't call the updateRole mutation.
  // We only allow user additions and removals. So we need a version of this with a
  // callback to close the drawer.
  const updateRoleUserIdsMutationWitSuccessMessage = useOpMutation({
    apiEndpointName: 'updateRoleUserIds',
    queryKeysToInvalidate: [['listRoleUsers']],
    onSuccessCallback: () => {
      setSearchParams({});
    },
  });

  const createRoleMutation = useOpMutation({
    apiEndpointName: 'createRole',
    queryKeysToInvalidate: [['listRoles'], ['describeRole']],
    onSuccessCallback: async (response, args) => {
      const newRoleId = response?.id!;
      const followUpMutations: Promise<unknown>[] = [];

      if (args.roleScopes) {
        followUpMutations.push(
          setRoleScopeIdsMutation.mutateAsync({
            apiEndpointRequirements: [orgId, newRoleId],
            payload: { scopeIds: args.roleScopes },
          }),
        );
      }

      if (args.sites) {
        followUpMutations.push(
          setRoleSiteIdsMutation.mutateAsync({
            apiEndpointRequirements: [orgId, newRoleId],
            payload: { siteIds: args.sites },
          }),
        );
      }

      if (args.users) {
        followUpMutations.push(
          setRoleUserIdsMutation.mutateAsync({
            apiEndpointRequirements: [orgId, newRoleId],
            payload: { userIds: args.users },
          }),
        );
      }

      await Promise.all(followUpMutations);
      setSearchParams({});
    },
  });

  const updateRoleMutation = useOpMutation({
    apiEndpointName: 'updateRole',
    queryKeysToInvalidate: [['listRoles'], ['describeRole']],
    onSuccessCallback: async (_response, args) => {
      const followUpMutations: Promise<unknown>[] = [];

      if (args.roleScopes) {
        followUpMutations.push(
          setRoleScopeIdsMutation.mutateAsync({
            apiEndpointRequirements: [orgId, roleId!],
            payload: { scopeIds: args.roleScopes },
          }),
        );
      }

      if (args.sites) {
        followUpMutations.push(
          setRoleSiteIdsMutation.mutateAsync({
            apiEndpointRequirements: [orgId, roleId!],
            payload: { siteIds: args.sites },
          }),
        );
      }

      if (args.users?.add?.length > 0 || args.users?.remove?.length > 0) {
        followUpMutations.push(
          updateRoleUserIdsMutation.mutateAsync({
            apiEndpointRequirements: [orgId, roleId!],
            payload: args.users,
          }),
        );
      }

      await Promise.all(followUpMutations);
      setSearchParams({});
    },
  });

  /* Submit handler */
  const handleSubmit = async ({ touchedValues }: OnSubmitArgs) => {
    const {
      name,
      description,
      isMfaRequired,
      isSiteSpecific,
      sites,
      users,
      roleScopes,
    } = touchedValues;

    const payload = {
      name,
      description,
      isMfaRequired,
      isSiteSpecific,
    };

    if (roleId) {
      if (isEditable) {
        await updateRoleMutation.mutateAsync({
          apiEndpointRequirements: [orgId, roleId!],
          payload,
          sites: isSiteSpecific === false ? [] : sites,
          roleScopes,
          users,
        });
      } else {
        // only allow user add and removal for roles that arent editable
        await updateRoleUserIdsMutationWitSuccessMessage.mutateAsync({
          apiEndpointRequirements: [orgId, roleId!],
          payload: users,
        });
      }
    } else {
      await createRoleMutation.mutateAsync({
        apiEndpointRequirements: [orgId],
        payload,
        sites: isSiteSpecific === false ? [] : sites,
        roleScopes,
        users: users?.add,
      });
    }
  };

  return {
    handleSubmit,
  };
};
